import { motion } from "framer-motion";
import { ArrowRight, Target, Cpu, Users, Lightbulb, TrendingUp, CheckCircle, Zap, Eye, Settings, BarChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { AnimatedSection } from "@/components/ui/AnimatedSection";

const values = [
  {
    icon: Settings,
    title: "Systems Over Hustle",
    description: "We believe growth should be engineered, not forced. If something requires constant manual effort, it's broken.",
  },
  {
    icon: Eye,
    title: "Clarity Beats Complexity",
    description: "More tools don't mean better results. Clear systems outperform messy stacks every time.",
  },
  {
    icon: Cpu,
    title: "AI Is a Lever, Not a Buzzword",
    description: "We use AI where it creates real leverage — not for show, not for noise.",
  },
  {
    icon: BarChart,
    title: "Outcomes Over Activity",
    description: "Traffic, impressions, and clicks don't pay the bills. Conversations, pipelines, and revenue do.",
  },
  {
    icon: TrendingUp,
    title: "Build Once. Improve Forever.",
    description: "We design systems that compound — not campaigns that reset.",
  },
];

const expertise = [
  "SEO & Answer Engine Optimization",
  "Account-Based Marketing",
  "Outbound systems & deliverability",
  "Paid media & funnel optimization",
  "Automation, CRM, and integrations",
];

const whyClients = [
  "They want predictable demand, not random leads",
  "They want leverage, not more headcount",
  "They want systems, not task execution",
  "They want to be future-proof as AI reshapes marketing",
];

export default function About() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-background pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-sm font-medium mb-8">
              About Us
            </span>
            <h1 className="heading-hero mb-6">
              We're Building the Next Generation of
              <br />
              <span className="text-muted-foreground">Marketing Systems</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              TheCloudSEO is an AI-First Marketing Agency built for companies that want 
              predictable growth without scaling headcount.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20">
        <div className="container-wide">
          <AnimatedSection className="max-w-4xl mx-auto">
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
              We don't believe marketing should be a collection of disconnected channels, tools, or tactics.
              <span className="text-foreground font-medium"> It should be a system</span> — designed once, improved continuously, and powered by AI.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* What We Focus On */}
      <section className="py-20 bg-secondary/30">
        <div className="container-wide">
          <AnimatedSection className="max-w-4xl mx-auto">
            <h2 className="heading-subsection mb-10 text-center">That's why we focus on:</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "AI-assisted SEO & Answer Engine Optimization", icon: Target },
                { title: "Account-based targeting instead of generic traffic", icon: Users },
                { title: "Automated outbound instead of manual follow-ups", icon: Zap },
                { title: "Signal-driven decisions instead of guesswork", icon: Lightbulb },
              ].map((item, index) => (
                <AnimatedSection key={item.title} delay={index * 0.1}>
                  <div className="flex items-start gap-4 p-6 rounded-2xl bg-background border border-border">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-lg font-medium pt-2">{item.title}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
            <AnimatedSection delay={0.4} className="mt-10 text-center">
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Our work sits at the intersection of marketing, automation, and AI infrastructure — 
                helping businesses stay visible, relevant, and scalable in a world where buyers no longer search the way they used to.
              </p>
            </AnimatedSection>
          </AnimatedSection>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 block">
                Our Mission
              </span>
              <h2 className="heading-section mb-6">
                To help businesses grow by designing AI-driven marketing systems
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Systems that replace manual effort, reduce dependency on headcount, and create consistent demand.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <span className="text-sm font-semibold text-primary-foreground/70 uppercase tracking-wider mb-4 block">
                Our Approach
              </span>
              <h2 className="heading-section mb-6">
                Most agencies execute campaigns.
                <br />
                <span className="text-primary-foreground/70">We architect marketing infrastructure.</span>
              </h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: "Clarity", subtitle: "over complexity" },
                { title: "Systems", subtitle: "over tactics" },
                { title: "Automation", subtitle: "over effort" },
                { title: "Outcomes", subtitle: "over activity" },
              ].map((item, index) => (
                <AnimatedSection key={item.title} delay={index * 0.1}>
                  <div className="p-6 rounded-2xl bg-primary-foreground/10 border border-primary-foreground/20 text-center">
                    <h3 className="text-2xl font-bold mb-1">{item.title}</h3>
                    <p className="text-primary-foreground/70">{item.subtitle}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection delay={0.5} className="text-center mt-12">
              <p className="text-xl text-primary-foreground/80">
                The result is marketing that compounds instead of resets every month.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-24">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="mb-12">
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 block">
                Founder Story
              </span>
              <h2 className="heading-section mb-6">
                Built From the Need for Leverage
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={0.1} className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl text-muted-foreground mb-6">
                TheCloudSEO was founded with a simple realization: Marketing teams are overworked, 
                under-leveraged, and stuck managing tools instead of building systems.
              </p>
              <p className="text-xl text-muted-foreground mb-6">
                After years of working across SEO, cloud infrastructure, automation, and AI-driven workflows, 
                it became clear that the future of marketing wouldn't belong to the biggest teams — 
                <span className="text-foreground font-medium"> but to the most systemized ones.</span>
              </p>
              <div className="p-8 rounded-3xl bg-secondary/50 my-10">
                <p className="text-2xl font-semibold text-center italic">
                  "AI wasn't going to replace marketers. But marketers who used AI would replace those who didn't."
                </p>
              </div>
              <p className="text-xl text-muted-foreground mb-6">
                TheCloudSEO was created to help companies:
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Stay visible as search shifts to AI answers",
                  "Replace repetitive outreach with automation",
                  "Focus human effort where it actually matters",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-lg">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xl text-muted-foreground">
                Today, the agency works with B2B companies across the US, Australia, India, and global markets — 
                helping founders and teams move from manual marketing to engineered growth.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-secondary/30">
        <div className="container-wide">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 block">
                Our Team
              </span>
              <h2 className="heading-section mb-6">
                Small by Design.
                <br />
                <span className="text-muted-foreground">Scalable by Systems.</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                We're not a large agency — and that's intentional. TheCloudSEO operates as a lean, 
                AI-enabled team supported by automation, frameworks, and repeatable systems.
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                "Move faster than traditional agencies",
                "Stay deeply involved in strategy",
                "Deliver senior-level thinking without bloated costs",
              ].map((item, index) => (
                <AnimatedSection key={item} delay={index * 0.1}>
                  <div className="p-6 rounded-2xl bg-background border border-border text-center">
                    <CheckCircle className="w-8 h-8 text-primary mx-auto mb-4" />
                    <p className="font-medium">{item}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection delay={0.3}>
              <div className="p-8 rounded-3xl bg-background border border-border">
                <h3 className="text-xl font-semibold mb-6 text-center">Our team combines expertise across:</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {expertise.map((item) => (
                    <span key={item} className="px-4 py-2 rounded-full bg-secondary text-sm font-medium">
                      {item}
                    </span>
                  ))}
                </div>
                <p className="text-center text-muted-foreground mt-6">
                  Every system we build for clients is one we believe in — and use ourselves.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24">
        <div className="container-wide">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection className="text-center mb-16">
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 block">
                Our Values
              </span>
              <h2 className="heading-section">What We Stand For</h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, index) => (
                <AnimatedSection key={value.title} delay={index * 0.1}>
                  <div className="premium-card p-8 h-full">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                      <value.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                    <p className="text-muted-foreground">{value.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Clients Work With Us */}
      <section className="py-24 bg-secondary/30">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection className="mb-12">
              <h2 className="heading-section mb-6">Why Clients Work With Us</h2>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 gap-4">
              {whyClients.map((item, index) => (
                <AnimatedSection key={item} delay={index * 0.1}>
                  <div className="flex items-center gap-4 p-5 rounded-2xl bg-background border border-border">
                    <CheckCircle className="w-6 h-6 text-primary flex-shrink-0" />
                    <p className="font-medium text-left">{item}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="container-narrow text-center">
          <AnimatedSection>
            <p className="text-xl text-muted-foreground mb-6">
              If you believe marketing should work <span className="text-foreground font-medium">with you</span>, not <span className="text-foreground font-medium">because of you</span>,
              <br />you'll feel at home at TheCloudSEO.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Button
                size="lg"
                className="rounded-full px-10 text-base h-14"
                data-cal-link="thecloudseo/30-min-meeting"
                data-cal-namespace="30-min-meeting"
                data-cal-config='{"layout":"month_view"}'
              >
                Book an AI Growth Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
